import { useState, useRef, useEffect, useMemo } from 'react';

export const useMapIntegration = (gpsMessages, currentVideoTime) => {
  const [routePolyline, setRoutePolyline] = useState(null);
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);

  const currentGpsMessage = useMemo(() => {
    if (gpsMessages.length === 0) return null;
    const currentTimeMicros = currentVideoTime * 1000000;
    return gpsMessages.reduce((prev, curr) => 
      Math.abs(curr.timestamp - currentTimeMicros) < Math.abs(prev.timestamp - currentTimeMicros) ? curr : prev
    );
  }, [gpsMessages, currentVideoTime]);

  // Update map center and route when GPS message changes
  useEffect(() => {
    if (mapRef.current && gpsMessages.length > 0) {
      if (currentGpsMessage) {
        mapRef.current.setCenter({ 
          lat: currentGpsMessage.latitude, 
          lng: currentGpsMessage.longitude 
        });
        if (!mapRef.current.getZoom() || mapRef.current.getZoom() === 5) {
          mapRef.current.setZoom(14);
        }
      }
      
      const currentTimeMicros = currentVideoTime * 1000000;
      const routePoints = gpsMessages
        .filter(msg => msg.timestamp <= currentTimeMicros)
        .map(msg => ({ lat: msg.latitude, lng: msg.longitude }));
      
      if (routePoints.length > 1) {
        if (routePolyline) {
          routePolyline.setPath(routePoints);
        } else {
          const polyline = new window.google.maps.Polyline({
            path: routePoints,
            geodesic: true,
            strokeColor: '#FF0000',
            strokeOpacity: 1.0,
            strokeWeight: 3
          });
          polyline.setMap(mapRef.current);
          setRoutePolyline(polyline);
        }
      } else if (routePolyline) {
        routePolyline.setMap(null);
        setRoutePolyline(null);
      }
    }
  }, [currentVideoTime, gpsMessages, currentGpsMessage, routePolyline]);

  const clearRoute = () => {
    if (routePolyline) {
      routePolyline.setMap(null);
      setRoutePolyline(null);
    }
  };

  const recreateRoute = () => {
    // Clear existing polyline first
    if (routePolyline) {
      routePolyline.setMap(null);
      setRoutePolyline(null);
    }
    
    if (mapRef.current && gpsMessages.length > 0) {
      const currentTimeMicros = currentVideoTime * 1000000;
      const routePoints = gpsMessages
        .filter(msg => msg.timestamp <= currentTimeMicros)
        .map(msg => ({ lat: msg.latitude, lng: msg.longitude }));
      
      if (routePoints.length > 1) {
        const polyline = new window.google.maps.Polyline({
          path: routePoints,
          geodesic: true,
          strokeColor: '#FF0000',
          strokeOpacity: 1.0,
          strokeWeight: 3
        });
        polyline.setMap(mapRef.current);
        setRoutePolyline(polyline);
      }
    }
  };

  return {
    currentGpsMessage,
    mapRef,
    mapContainerRef,
    clearRoute,
    recreateRoute
  };
};